import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArtistSearchResult,
  ClientArtistListItem,
  ClientCoral,
  Source,
} from 'shared/types/platformTypes';
import {     
    SignalRecord
  } from 'shared/types/contentTypes';
import useCoralEditor from '../../hooks/useCoralEditor';
import { useAuthContext } from '../Auth/AuthContext/AuthContext';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import ArtistSearch from '../../components/ArtistSearch/ArtistSearch';
import CoralButton from '../../components/CoralButtons/CoralButton';
import Spinner from '../../components/Spinner/Spinner';
import { Tooltip } from 'react-tooltip';
import {
  Edit2,
  ArrowUp,
  HelpCircle, // Add this import
  Search,
  Minus
} from 'lucide-react';
import selectedStar from '../../assets/images/selected-star.png';
import unSelectedStar from '../../assets/images/unselected-star.png';
import disabledIcon from '../../assets/images/disabled-icon.png';
import plusIcon from '../../assets/images/create-coral/add-artist.png';
import AddedTick from '../AddedTick/AddedTick';
import EditorialArtistAvatar from './EditorialArtistAvatar';
import ContributionFeeBreakdown from '../ContributionFeeBreakdown/ContributionFeeBreakdown';
import { creatorBreakdown } from './creatorFeePreview';
import { safeBreakdown } from '../ContributionFeeBreakdown/feeBreakdownGuards';
import { useNavigate } from 'react-router-dom';

// Remove local interface since we're using the shared one

interface SignalState {
  artistSignals: SignalRecord[];
  coralSignal?: SignalRecord;
}

interface AllInOneCoralCreatorProps {
  artistId?: string;
  sharedCoralId?: string;
  existingCoralId?: string;
  transparent?: boolean;
  lightMode?: boolean;
  compact?: boolean;
  /** Home2 base1 editorial reskin. Default false — when false every styled
   *  element renders the exact pre-existing classes (byte-identical for
   *  legacy Home and the other shared consumers). */
  editorial?: boolean;
  onCoralLoaded?: (coral: ClientCoral) => void;
  onBack?: () => void;
}

// Editorial (Home2) slider anchor — the contribution slider starts here and
// cannot go below it. Only used when `editorial` is true.
const EDITORIAL_MIN_PLEDGE = 4;

const AllInOneCoralCreator: React.FC<AllInOneCoralCreatorProps> = ({
  artistId,
  sharedCoralId,
  existingCoralId,
  transparent = false,
  lightMode = false,
  compact = false,
  editorial = false,
  onCoralLoaded,
  onBack,
}) => {
  const { userProfile } = useAuthContext();
  const {
    coralData,
    addArtistDirectly,
    removeArtist,
    updatePledgeAmount,
    saveCoral,
    simulateCoral,
    simulationResults,
    addPinnedArtist,
    removePinnedArtist,
    updateAppreciationSignals,
  } = useCoralEditor(
    existingCoralId || sharedCoralId || '',
    !!sharedCoralId && !existingCoralId,
    (!existingCoralId && !sharedCoralId) ? artistId : undefined,
    !!sharedCoralId && !existingCoralId // Only create as user coral when it's a shared coral
  );

  const [pledgeValue, setPledgeValue] = useState('');
  const [isEditingPledge, setIsEditingPledge] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [isAddingArtist, setIsAddingArtist] = useState(false);
  // Editorial-only: edit mode (variant D). Rows are clean while reading; arming
  // Edit reveals a leading − on each row so an artist can be removed deliberately.
  const [isEditingArtists, setIsEditingArtists] = useState(false);

  const [signalState, setSignalState] = useState<SignalState>({
    artistSignals: [],
  });
  const [expandedSignalId, setExpandedSignalId] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleFeesToggle = (open: boolean) => {
    trackEvent(
      open ? EventName.CORAL_FEES_EXPANDED : EventName.CORAL_FEES_COLLAPSED,
      { userId: userProfile?.userId }
    );
  };

  const selectedArtists = useMemo(
    () =>
      coralData?.artistPool?.artistList.filter(
        (artist) => artist.selected || artist.source === Source.USER
      ) || [],
    [coralData?.artistPool?.artistList]
  );

  // Edit mode only has meaning in editorial mode and only with rows to edit.
  const editingArtists =
    editorial && isEditingArtists && selectedArtists.length > 0;

  // Leaving the list empty (or switching out of editorial) should drop the
  // armed Edit state so a freshly added artist never reappears mid-edit.
  useEffect(() => {
    if (isEditingArtists && selectedArtists.length === 0) {
      setIsEditingArtists(false);
    }
  }, [isEditingArtists, selectedArtists.length]);

  useEffect(() => {
    if (coralData) {
      setPledgeValue(coralData.pledgeAmount.toString());

      // Ensure we simulate when loading an existing coral
      if (
        coralData.pledgeAmount > 0 &&
        coralData.artistPool?.artistList.length > 0
      ) {
        simulateCoral();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coralData]);

  const handleUpdatePledgeAmount = useCallback(async () => {
    const decimalRegex = /^([1-9]\d*(\.\d{1,2})?)?$/;

    if (decimalRegex.test(pledgeValue)) {
      const amount = Number(pledgeValue);
      if (amount < 1 || amount > 70) {
        setValidationMessage('Amount must be between $1 and $70');
      } else {
        setValidationMessage('');
        setIsEditingPledge(false);

        const oldAmount = coralData?.pledgeAmount || 0;
        await updatePledgeAmount(amount);

        trackEvent(EventName.CORAL_PLEDGE_AMOUNT_CHANGED, {
          userId: userProfile?.userId,
          oldAmount,
          newAmount: amount,
        });
      }
    } else {
      setValidationMessage('Invalid pledge amount');
    }
  }, [pledgeValue, updatePledgeAmount, userProfile, coralData]);

  // Editorial-only: anchor the contribution at $4 once when the coral loads
  // with no amount set, so the slider has a sensible start and the simulator
  // produces allocations immediately. Ref-guarded so it fires at most once.
  const editorialAnchorApplied = useRef(false);
  useEffect(() => {
    if (
      editorial &&
      !editorialAnchorApplied.current &&
      coralData &&
      coralData.pledgeAmount === 0
    ) {
      editorialAnchorApplied.current = true;
      updatePledgeAmount(EDITORIAL_MIN_PLEDGE);
    }
  }, [editorial, coralData, updatePledgeAmount]);

  // Editorial slider value — the live pledge, clamped to the slider range.
  const editorialPledge = Math.min(
    70,
    Math.max(
      EDITORIAL_MIN_PLEDGE,
      coralData?.pledgeAmount && coralData.pledgeAmount > 0
        ? coralData.pledgeAmount
        : EDITORIAL_MIN_PLEDGE
    )
  );

  // Synchronous mirror so the slider thumb tracks the pointer immediately.
  // updatePledgeAmount is debounced (~300ms); without this the thumb is bound
  // to the lagging coralData value and visibly stutters/snaps back during a
  // drag. Once the debounced update lands and coralData catches up, the
  // override is cleared so external resets (coral reload) flow through again.
  const [editorialSliderValue, setEditorialSliderValue] = useState<number | null>(null);
  const sliderDisplayValue = editorialSliderValue ?? editorialPledge;

  useEffect(() => {
    if (editorialSliderValue !== null && coralData?.pledgeAmount === editorialSliderValue) {
      setEditorialSliderValue(null);
    }
  }, [coralData?.pledgeAmount, editorialSliderValue]);

  const handleEditorialPledgeSlider = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Math.min(
      70,
      Math.max(EDITORIAL_MIN_PLEDGE, parseInt(e.target.value, 10) || EDITORIAL_MIN_PLEDGE)
    );
    setEditorialSliderValue(value);
    setPledgeValue(String(value));
    setValidationMessage('');
    updatePledgeAmount(value);
    trackEvent(EventName.CORAL_PLEDGE_AMOUNT_CHANGED, {
      userId: userProfile?.userId,
      oldAmount: coralData?.pledgeAmount || 0,
      newAmount: value,
    });
  };

  const handleAddArtist = useCallback(
    async (artistName: string, artistGuid?: string) => {
      if (artistName.trim()) {
        try {
          await addArtistDirectly(artistName.trim(), artistGuid);
          setIsAddingArtist(false);
          trackEvent(EventName.CORAL_ARTIST_ADDED, {
            userId: userProfile?.userId,
            artistName: artistName.trim(),
            artistId: artistGuid,
            totalArtists: selectedArtists.length + 1,
          });
        } catch (error) {
          console.error('Error adding artist:', error);
        }
      }
    },
    [addArtistDirectly, userProfile, selectedArtists.length]
  );

  const handleSelectArtist = useCallback(
    (artist: ArtistSearchResult) => {
      handleAddArtist(artist.name, artist.guid);
    },
    [handleAddArtist]
  );

  const handleAddToEcosystem = useCallback(async () => {
    try {
      const savedCoralGuid = await saveCoral();
      trackEvent(EventName.CORAL_GOTO_PAYMENT, {
        userId: userProfile?.userId,
        coralId: savedCoralGuid!,
        pledgeAmount: coralData!.pledgeAmount,
        totalArtists: selectedArtists.length,
      });
      navigate(`/payment/${savedCoralGuid}?amt=${coralData?.pledgeAmount}`);
    } catch (error) {
      console.error('Error saving coral:', error);
    }
  }, [saveCoral, userProfile, coralData, selectedArtists.length, navigate]);

  const renderArtistStatus = useCallback((artist: ClientArtistListItem) => {
    if (artist.artistListItemStatus === 'pending') {
      return (
        <button
          data-tooltip-id="searchingTooltip"
          data-tooltip-content="We're searching for this artist in our database. Try a different spelling if nothing returns."
        >
          <Spinner className="w-5 h-5 animate-pulse" />
          <Tooltip id="searchingTooltip" />
        </button>
      );
    } else if (artist.artistListItemStatus === 'not found') {
      return <img src={plusIcon} alt="Add artist" className="w-5 h-5" />;
    } else if (artist.artistListItemActive === false) {
      return (
        <button
          data-tooltip-id="disabledIconTooltip"
          data-tooltip-content={
            artist.artistListItemStatus === 'top-artist'
              ? 'This artist has reached high levels of commercial success and is not eligible for wearecoral.'
              : artist.artistListItemStatus === 'deceased'
              ? "wearecoral is for living artists as it's about supporting a thriving ecosystem."
              : 'This artist is not eligible for wearecoral.'
          }
        >
          <img src={disabledIcon} alt="Artist is disabled" className="w-4 h-4" />
          <Tooltip id="disabledIconTooltip" />
        </button>
      );
    } else {
      // Editorial mode uses the softer editorial-accent (#D88565, same family
      // as HexBloom's `deep`). Legacy/platform mode keeps the brand coral-pink
      // (#FC5373). Same vector — no asset divergence.
      return (
        <AddedTick
          colorClassName={editorial ? 'text-editorial-accent' : 'text-coral-pink'}
        />
      );
    }
  }, [editorial]);

  const handleRemoveArtist = useCallback(
    (artistName: string) => {
      // This handler already prompts, so skip the hook's own confirm to avoid a
      // double modal (the hook otherwise re-prompts for user-added artists).
      if (window.confirm('Are you sure you want to remove this artist?')) {
        removeArtist(artistName, true);
        trackEvent(EventName.CORAL_ARTIST_REMOVED, {
          userId: userProfile?.userId,
          artistName,
          totalArtists: selectedArtists.length - 1,
        });
      }
    },
    [removeArtist, userProfile, selectedArtists.length]
  );

  // Editorial edit-mode removal (variant D). No confirm dialog: arming Edit is
  // itself the deliberate gate, so the leading − removes directly (skipConfirm).
  const handleRemoveArtistEditing = useCallback(
    (artistName: string) => {
      removeArtist(artistName, true);
      trackEvent(EventName.CORAL_ARTIST_REMOVED, {
        userId: userProfile?.userId,
        artistName,
        totalArtists: selectedArtists.length - 1,
      });
    },
    [removeArtist, userProfile, selectedArtists.length]
  );

  const handleTogglePinArtist = useCallback(
    (artist: ClientArtistListItem) => {
      // Use artistId if available, otherwise use artistName (for pending artists)
      const pinKey = artist.artistId || artist.artistName!;
      const isPinned = coralData?.pinnedArtists.includes(pinKey) ||
        (artist.artistId && coralData?.pinnedArtists.includes(artist.artistId)) ||
        (artist.artistName && coralData?.pinnedArtists.includes(artist.artistName));

      if (coralData && isPinned) {
        removePinnedArtist(pinKey);
        trackEvent(EventName.CORAL_ARTIST_UNPINNED, {
          userId: userProfile?.userId,
          artistName: artist.artistName!,
          artistId: artist.artistId || '',
        });
      } else if (
        coralData &&
        coralData.pinnedArtists &&
        coralData.pinnedArtists.length < 2
      ) {
        addPinnedArtist(pinKey);
        trackEvent(EventName.CORAL_ARTIST_PINNED, {
          userId: userProfile?.userId,
          artistName: artist.artistName!,
          artistId: artist.artistId || '',
        });
      }
    },
    [coralData, addPinnedArtist, removePinnedArtist, userProfile]
  );

  // Determine container styling
  const containerClassName = editorial
    ? 'w-full p-6 md:p-7 text-editorial-muted font-sans'
    : compact
    ? 'w-full p-4 bg-white text-black'
    : transparent
    ? 'max-w-2xl min-w-xl mx-auto bg-white/10 backdrop-blur-md p-4 py-6 rounded-lg text-white'
    : 'max-w-2xl min-w-xl mx-auto p-6 bg-white rounded-lg shadow-lg text-black';

  const getTextColor = (defaultColor: string, transparentColor: string) => {
    return transparent ? transparentColor : defaultColor;
  };

  // Editorial reskin switch. `fallback` MUST be the exact pre-existing class
  // expression so that editorial=false is byte-identical for legacy Home and
  // the other shared consumers (Dashboard, PublicCoral, PublicArtist,
  // CoralCard).
  const ed = (editorialCls: string, fallback: string) =>
    editorial ? editorialCls : fallback;

  // Call onCoralLoaded when coral data is available
  useEffect(() => {
    if (coralData && onCoralLoaded) {
      onCoralLoaded(coralData);
    }
  }, [coralData, onCoralLoaded]);

  // Update handleUpdateAppreciationSignal to require artistId
  const handleUpdateArtistSignal = (message: string, artistId: string) => {
    setSignalState(prev => {
      const existing = prev.artistSignals.findIndex(m => m.artistId === artistId);
      const artist = selectedArtists.find(a => a.artistId === artistId);

      if (!artist) return prev;

      const newSignal: SignalRecord = {
        signalType: 'artist',
        artistId,
        artistName: artist.artistName!,
        message,
        coralId: coralData?.guid || '',
      };

      const newArtistSignals = existing >= 0
        ? prev.artistSignals.map((s, i) => i === existing ? newSignal : s)
        : [...prev.artistSignals, newSignal];

      const newState = { ...prev, artistSignals: newArtistSignals };
      
      // Fix 1: Add type narrowing for the signals array
      const allSignals = [...newArtistSignals, prev.coralSignal].filter(
        (signal): signal is SignalRecord => signal !== undefined
      );
      
      updateAppreciationSignals(allSignals);
      return newState;
    });
  };

  const handleUpdateCoralSignal = (message: string) => {
    setSignalState(prev => {
      const newSignal: SignalRecord = {
        signalType: 'coral',
        message,
        coralId: coralData?.guid || '',
      };

      const newState = { ...prev, coralSignal: newSignal };
      updateAppreciationSignals([...prev.artistSignals, newSignal]);
      return newState;
    });
  };

  const renderArtistSignalInput = (artist: ClientArtistListItem) => {
    const isExpanded = expandedSignalId === artist.artistId;
    const signal = signalState.artistSignals.find(s => s.artistId === artist.artistId);

    return (
      <div className="mt-2">
        {!isExpanded ? (
          <div className="flex items-start gap-2">
            <button
              onClick={() => {
                if (artist.artistId) {
                  setExpandedSignalId(artist.artistId);
                }
              }}
              className="text-sm text-coral-pink/70 hover:text-coral-orange flex items-start gap-1 transition-colors duration-200 text-left"
            >
              <span className="text-left">Why are you backing {artist.artistName}? (optional)</span>
            </button>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <textarea
              placeholder={`Your financial contribution helps them create. Share your reasons to help others discover why.`}
              value={signal?.message || ''}
              onChange={(e) => handleUpdateArtistSignal(e.target.value, artist.artistId!)}
              onBlur={() => {
                if (!signal?.message) setExpandedSignalId(null);
              }}
              className={`w-full p-3 rounded-lg resize-none transition-all duration-200
                ${transparent
                  ? 'bg-black/60 backdrop-blur-sm border border-gray-500 text-white placeholder-gray-400 focus:border-coral-pink'
                  : 'bg-gray-80 border border-gray-200 text-gray-900 placeholder-gray-500 focus:border-coral-orange'
                } focus:ring-1 focus:ring-coral-pink`}
              rows={2}
              maxLength={300}
              autoFocus
            />
            <div className="flex justify-between items-center text-xs text-gray-400">
              <span>{signal?.message?.length || 0}/300</span>
              <button
                onClick={() => setExpandedSignalId(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderArtistRow = (artist: ClientArtistListItem, index: number) => {
    // Match by artistId, or by artist_name for pending artists without an ID
    const allocatedAmount = simulationResults.artistAllocations.find(
      (a) => (artist.artistId && a.artist_id === artist.artistId) ||
             (a.artist_name === artist.artistName)
    )?.allocated_amount;

    const isPinned =
      (artist.artistId && coralData?.pinnedArtists.includes(artist.artistId)) ||
      (artist.artistName && coralData?.pinnedArtists.includes(artist.artistName));
    const starSrc = isPinned ? selectedStar : unSelectedStar;
    const amountText =
      coralData && coralData.pledgeAmount > 0
        ? `$${(allocatedAmount || 0).toFixed(2)}`
        : '';
    const genre = artist.artistData?.primaryGenre;
    const isSearching = artist.artistListItemStatus === 'pending';
    const onRowClick = (e: React.MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.pin-star')) {
        handleRemoveArtist(artist.artistName!);
      }
    };
    const onStarClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleTogglePinArtist(artist);
    };

    return (
      <div key={index} className={compact ? "" : "mb-6"}>
        {editorial ? (
          <div
            className={`flex items-center gap-3 rounded-lg px-3 py-2 border transition-colors ${
              editingArtists
                ? 'bg-white border-editorial-muted/10'
                : 'bg-ivory/60 border-transparent'
            }`}
          >
            {/* Edit mode (variant D): a leading − is the only remove affordance;
                it disappears entirely from the resting state, so the resting row
                gives the artist name the full width it needs. */}
            {editingArtists && (
              <button
                type="button"
                onClick={() => handleRemoveArtistEditing(artist.artistName!)}
                aria-label={`Remove ${artist.artistName}`}
                className="flex-none w-5 h-5 rounded-full bg-coral-red text-white inline-flex items-center justify-center hover:bg-coral-red/90 transition-colors"
              >
                <Minus size={12} strokeWidth={2.6} />
              </button>
            )}
            <EditorialArtistAvatar artist={artist} />
            <div className="min-w-0 mr-auto">
              <p className="text-sm text-editorial-muted leading-tight truncate">
                {artist.artistName}
              </p>
              {isSearching ? (
                <p className="text-[11px] text-editorial-muted/55 truncate">Searching…</p>
              ) : genre ? (
                <p className="text-[11px] text-editorial-muted/55 truncate">{genre}</p>
              ) : null}
            </div>
            <span className="text-sm whitespace-nowrap font-cormorant text-editorial-muted tabular-nums flex-none">
              {amountText}
            </span>
            {/* Pin lives only in the resting state; Edit mode hides it so the
                row's single armed action is remove. */}
            {!editingArtists && (
              <img
                src={starSrc}
                alt="Pin artist"
                className="w-4 h-4 cursor-pointer flex-shrink-0 pin-star"
                onClick={onStarClick}
              />
            )}
          </div>
        ) : (
          <div
            className={`flex items-start justify-between py-2 border-b ${getTextColor(
              'border-gray-200',
              'border-gray-600'
            )} cursor-pointer hover:bg-gray-50/20 dark:hover:bg-gray-800/20`}
            onClick={onRowClick}
          >
            <div className="flex items-start flex-grow mr-2 min-w-0">
              <img
                src={starSrc}
                alt="Pin artist"
                className="w-6 h-6 cursor-pointer mr-4 flex-shrink-0 pin-star"
                onClick={onStarClick}
              />
              <span
                className={`font-medium break-words ${getTextColor(
                  'text-gray-900',
                  'text-white'
                )}`}
              >
                {artist.artistName}
              </span>
            </div>
            <div className="flex items-center flex-shrink-0 w-24 justify-end">
              <span
                className={`mr-2 text-sm whitespace-nowrap ${getTextColor(
                  'text-gray-600',
                  'text-gray-300'
                )}`}
              >
                {amountText}
              </span>
              <div className="w-5 h-5 flex-shrink-0">{renderArtistStatus(artist)}</div>
            </div>
          </div>
        )}

        {/* Show appreciation textarea only in non-compact mode */}
        {!compact && renderArtistSignalInput(artist)}
      </div>
    );
  };

  const renderCoralSignalInput = () => {
    const isExpanded = expandedSignalId === 'coral';
    const signal = signalState.coralSignal;

    if (!selectedArtists.length || !coralData?.pledgeAmount) return null;

    return (
      <div className="mb-6 mt-6">
        {!isExpanded ? (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setExpandedSignalId('coral')}
              className="text-sm text-coral-pink/70 hover:text-coral-orange flex items-center gap-1 transition-colors duration-200"
            >
              <span>Add your intention for this coral (optional)</span>
            </button>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-start">
              <textarea
                placeholder="Actions taken with clear intention are powerful. Add yours to signal to others the future you're creating."
                value={signal?.message || ''}
                onChange={(e) => handleUpdateCoralSignal(e.target.value)}
                onBlur={() => {
                  if (!signal?.message) setExpandedSignalId(null);
                }}
                className={`w-full p-3 rounded-lg resize-none transition-all duration-200
                  ${transparent
                    ? 'bg-black/60 backdrop-blur-sm border border-gray-500 text-white placeholder-gray-400 focus:border-coral-pink'
                    : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500 focus:border-coral-orange'
                  } focus:ring-1 focus:ring-coral-pink`}
                rows={3}
                maxLength={300}
                autoFocus
              />
              <button
                className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                data-tooltip-id="coralSignalInfoTooltip"
                data-tooltip-content="Share the story behind your coral. What connects these artists for you? Your perspective helps others discover new connections in music."
              >
                <HelpCircle size={16} />
                <Tooltip id="coralSignalInfoTooltip" />
              </button>
            </div>
            <div className="flex justify-between items-center text-xs text-gray-400">
              <span>{signal?.message?.length || 0}/300</span>
              <button
                onClick={() => setExpandedSignalId(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ----
  // PROGRESS INDICATOR LOGIC
  // ----
  const steps = 2;
  let complete = 0;
  if (selectedArtists.length > 0) complete++;
  if (coralData?.pledgeAmount && coralData.pledgeAmount > 0) complete++;
  const progress = (complete / steps) * 100;

  // Modify the helper for step text
  const getStepText = () => {
    if (selectedArtists.length === 0) {
      return "1 of 2 steps (Add an artist)";
    }
    if (!coralData?.pledgeAmount || coralData.pledgeAmount === 0) {
      return "2 of 2 steps (Set monthly amount)";
    }
    return "Ready to join the ecosystem!";
  };

  return (
    <div className={containerClassName} data-testid="coral-creator">
      {/* Header with optional back button */}
      <div className={compact ? "mb-4" : "mb-6"}>
        {onBack && (!compact || existingCoralId) && (
          <div className="flex items-center mb-3">
            <button
              onClick={onBack}
              className="flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Return
            </button>
          </div>
        )}
        
        <div className={ed('font-cormorant text-2xl md:text-3xl text-editorial-muted leading-tight tracking-tight', `font-nunito ${compact ? "text-lg" : "text-xl"} font-bold bg-clip-text text-transparent bg-gradient-to-r from-coral-pink to-coral-orange`)}>
          {existingCoralId ? "Finalise Your Coral" : "Personalise Your Coral"}
        </div>

        {editorial && (
          <p className="mt-2 text-sm text-editorial-muted/70 leading-relaxed">
            A coral is a collection of artists you back monthly. Add artists, set one amount.
          </p>
        )}
        {!editorial && !compact && (
          <>
            <p
              className={`font-nunito text-base ${getTextColor(
                'text-gray-400',
                'text-gray-300'
              )}`}
            >
              A coral is the collection of artists you back monthly
            </p>
            <div className="mt-3 w-full bg-gray-200 rounded-full h-2 relative">
              <div
                className="bg-coral-orange h-2 rounded-full absolute top-0 left-0 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 mt-1 text-right">
              {getStepText()}
            </p>
          </>
        )}
      </div>

      <div className="w-full mb-8">
        {editorial ? (
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[11px] tracking-[0.18em] uppercase text-editorial-muted/60 font-medium">
              Add artists
            </h4>
            {selectedArtists.length > 0 && (
              <button
                type="button"
                onClick={() => setIsEditingArtists((v) => !v)}
                className="text-xs font-medium text-editorial-accent hover:text-editorial-accent/80 transition-colors p-0.5"
              >
                {editingArtists ? 'Done' : 'Edit'}
              </button>
            )}
          </div>
        ) : (
          <h4
            className={`font-nunito text-sm font-bold uppercase ${getTextColor(
              'text-gray-400',
              'text-gray-300'
            )}`}
          >
            Artists
          </h4>
        )}
        <div className={ed('mb-4 space-y-2', 'mb-4')}>
          {!coralData ? (
            // Loading skeleton
            <div className="animate-pulse flex items-start justify-between py-2 border-b border-gray-200">
              <div className="flex items-start">
                <div className="w-6 h-6 rounded-full bg-gray-200 mr-4" />
                <div className="w-32 h-5 bg-gray-200 rounded" />
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-16 h-5 bg-gray-200 rounded" />
                <div className="w-5 h-5 bg-gray-200 rounded-full" />
              </div>
            </div>
          ) : (
            selectedArtists.map((artist, index) => renderArtistRow(artist, index))
          )}
        </div>
        {editorial ? (
          <div className="relative [&_input]:!bg-ivory/50 [&_input]:!border-editorial-muted/15 [&_input]:!rounded-xl [&_input]:!py-3 [&_input]:!pl-10 [&_input]:!text-editorial-muted [&_input]:!placeholder-editorial-ink/35 [&_input]:!ring-0 [&_input]:focus:!border-editorial-muted/40 [&_ul]:!border-editorial-muted/15 [&_ul]:!rounded-xl [&_li]:!text-editorial-muted">
            <Search
              size={16}
              strokeWidth={2}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-editorial-muted/40 z-10 pointer-events-none"
            />
            <ArtistSearch
              onSelect={handleSelectArtist}
              onSubmit={handleAddArtist}
              placeholder="Search for an artist…"
            />
          </div>
        ) : isAddingArtist ? (
          <ArtistSearch
            onSelect={handleSelectArtist}
            onSubmit={handleAddArtist}
            placeholder="Enter artist name"
          />
        ) : (
          <button
  type="button"
  className={ed(
    'mt-1 w-full flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs text-editorial-muted/65 border border-dashed border-editorial-muted/25 hover:text-editorial-muted hover:border-editorial-muted/45 transition-colors',
    `
    inline-block
    px-3
    py-1
    text-sm
    font-medium
    text-coral-orange
    border
    border-coral-orange
    rounded-md
    hover:bg-coral-orange
    hover:text-white
    transition-colors
    duration-300
  `
  )}
  onClick={() => setIsAddingArtist(true)}
>
  + {selectedArtists.length === 0 ? 'Add artist' : 'Add more artists'}
</button>
        )}
      </div>

      {/* Bottom Section: Fees & CTA */}
      <div className={ed(`pt-6 border-t border-editorial-muted/10`, `${compact ? 'pt-4' : 'pt-8'} border-t ${getTextColor('border-gray-200', 'border-gray-600')}`)}>
        {/* Top Section: Monthly Contribution */}
        <div className="mb-1">
          {editorial && (
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] tracking-[0.18em] uppercase text-editorial-muted/60">
                  Monthly contribution
                </span>
                <span className="font-cormorant text-editorial-muted text-3xl tabular-nums leading-none">
                  ${sliderDisplayValue}
                  <span className="text-sm text-editorial-muted/50 font-sans"> /mo</span>
                </span>
              </div>
              <input
                type="range"
                min={EDITORIAL_MIN_PLEDGE}
                max={70}
                step={1}
                value={sliderDisplayValue}
                onChange={handleEditorialPledgeSlider}
                aria-label="Monthly contribution"
                className="mt-3 w-full accent-coral-orange cursor-pointer"
              />
              <div className="mt-1 flex justify-between text-[11px] text-editorial-muted/45 tabular-nums">
                <span>${EDITORIAL_MIN_PLEDGE}</span>
                <span>$70</span>
              </div>
            </div>
          )}
          {!editorial && (
          <>
          <div className="flex items-center justify-between">
            <h2
              className={ed(
                'text-[11px] tracking-[0.18em] uppercase text-editorial-muted/60 font-medium',
                `font-nunito text-base font-bold ${getTextColor(
                  'text-gray-900',
                  'text-white'
                )}`
              )}
            >
              Monthly Contribution{' '}
              <span className={ed('text-editorial-muted/45', getTextColor('text-gray-600', 'text-gray-300'))}>
                (AUD)
              </span>
            </h2>
            <div className="flex items-center ml-4">
              <span className={ed('font-cormorant text-editorial-muted text-3xl leading-none', getTextColor('text-gray-600', 'text-gray-300'))}>$</span>
              {isEditingPledge ? (
                <input
                  type="text"
                  inputMode="numeric"
                  value={pledgeValue}
                  onChange={(e) => setPledgeValue(e.target.value)}
                  onBlur={handleUpdatePledgeAmount}
                  onKeyPress={(e) => e.key === 'Enter' && handleUpdatePledgeAmount()}
                  className={ed(
                    'w-20 p-1 bg-transparent font-cormorant text-3xl text-editorial-muted tabular-nums border-b border-editorial-muted/25 focus:outline-none focus:border-editorial-muted/60 transition-colors',
                    `w-16 p-1 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      transparent
                        ? 'bg-white bg-opacity-20 text-white border-gray-500'
                        : 'border-gray-300'
                    }`
                  )}
                  autoFocus
                  placeholder=""
                />
              ) : (
                <div
                  className={ed(
                    `flex items-center cursor-pointer rounded px-2 py-1 transition-colors duration-200 ${
                      coralData?.pledgeAmount === 0 ? 'text-editorial-accent' : 'text-editorial-muted'
                    }`,
                    `flex items-center cursor-pointer rounded px-2 py-1 transition-colors duration-200 ${
                      coralData?.pledgeAmount === 0
                        ? 'text-coral-pink'
                        : getTextColor('text-gray-600', 'text-white')
                    }`
                  )}
                  onClick={() => {
                    if (coralData?.pledgeAmount === 0) {
                      setPledgeValue('');
                    } else {
                      setPledgeValue(coralData?.pledgeAmount.toFixed(2) || '');
                    }
                    setIsEditingPledge(true);
                  }}
                >
                  <span className={ed('font-cormorant text-3xl mr-2 tabular-nums leading-none', 'text-base mr-2')}>
                    {coralData?.pledgeAmount === 0 ? (
                      <i className={ed('text-editorial-accent text-base not-italic hidden sm:block', 'bg-clip-text text-transparent bg-gradient-to-r from-coral-pink to-coral-orange text-sm hidden sm:block')}>
                        Set amount
                      </i>
                    ) : (
                      coralData?.pledgeAmount.toFixed(2)
                    )}
                  </span>
                  <Edit2
                    size={editorial ? 16 : undefined}
                    className={
                      coralData?.pledgeAmount === 0
                        ? ed('text-editorial-accent animate-pulse', 'text-coral-pink animate-pulse')
                        : ed('text-editorial-muted/50', '')
                    }
                  />
                </div>
              )}
            </div>
          </div>
          {coralData?.pledgeAmount === 0 && (
            <p
              className={ed(
                'mt-2 text-center text-sm text-editorial-muted/55',
                `font-nunito mt-2 text-center text-sm ${getTextColor(
                  'text-gray-300',
                  'text-gray-300'
                )}`
              )}
            >
              Consider contributing the price of a single drink at a local gig
            </p>
          )}
          </>
          )}
          {validationMessage && (
            <p className="text-red-500 mt-2 text-sm">{validationMessage}</p>
          )}
        </div>

        {coralData && coralData.pledgeAmount > 0 && (
          <div
            className={ed(
              'mb-6 text-editorial-muted/70',
              `mb-6 ${getTextColor('text-gray-600', 'text-gray-300')}`
            )}
          >
            {(() => {
              const b = safeBreakdown(() =>
                creatorBreakdown(coralData.pledgeAmount, simulationResults)
              );
              return b ? (
                <ContributionFeeBreakdown
                  breakdown={b}
                  receiptHeading={`Where your $${coralData.pledgeAmount.toFixed(2)} goes`}
                  collapsible
                  editorial={editorial}
                  onToggle={handleFeesToggle}
                />
              ) : null;
            })()}
          </div>
        )}

        {/* Coral Signal Input - Hide in compact mode to save space */}
        {!compact && renderCoralSignalInput()}

        {/* CTA */}
        <div className="mt-4">
          <CoralButton
            onClick={handleAddToEcosystem}
            className={ed(
              `w-full px-5 py-3.5 !text-sm !font-medium !normal-case !tracking-normal relative overflow-hidden ${
                selectedArtists.length === 0 || coralData?.pledgeAmount === 0
                  ? '!bg-editorial-muted/10 !text-editorial-muted/50 cursor-not-allowed'
                  : '!bg-coral-orange !text-editorial-muted hover:!bg-coral-orange/90'
              }`,
              `w-full px-6 py-3 text-lg font-semibold relative overflow-hidden ${
                selectedArtists.length === 0 || coralData?.pledgeAmount === 0
                  ? lightMode
                    ? 'bg-gray-100 border-2 border-coral-pink border-opacity-30 cursor-not-allowed'
                    : 'opacity-90 cursor-not-allowed bg-gray-700'
                  : ''
              }`
            )}
            disabled={selectedArtists.length === 0 || coralData?.pledgeAmount === 0}
          >
            {selectedArtists.length === 0 ? (
              <span className="flex items-center justify-center">
                <span className={ed('mr-2 text-editorial-muted/70', `mr-2 ${lightMode ? 'text-coral-pink' : 'bg-clip-text text-transparent bg-gradient-to-r from-coral-pink to-coral-orange'}`)}>
                  Add an artist to start
                </span>
                <ArrowUp className={ed('w-4 h-4 animate-bounce text-editorial-muted/60', `w-4 h-4 animate-bounce ${lightMode ? 'text-coral-pink' : 'text-coral-orange'}`)} />
              </span>
            ) : coralData?.pledgeAmount === 0 ? (
              <span className="flex items-center justify-center">
                <span className={ed('mr-2 text-editorial-muted/70', `mr-2 ${lightMode ? 'text-coral-pink' : 'bg-clip-text text-transparent bg-gradient-to-r from-coral-pink to-coral-orange'}`)}>
                  Set an amount to continue
                </span>
                <ArrowUp className={ed('w-4 h-4 animate-bounce text-editorial-muted/60', `w-4 h-4 animate-bounce ${lightMode ? 'text-coral-pink' : 'text-coral-orange'}`)} />
              </span>
            ) : (
              existingCoralId ? 'Activate Your Coral' : 'Add Your Coral to the Ecosystem'
            )}
          </CoralButton>
        </div>
      </div>
    </div>
  );
};

export default AllInOneCoralCreator;